'use strict'
import { useState } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@material-ui/core'
import { formatShortDate } from './cadenceAnchorOptions'
// Report drawer for one auto-optimize run: summary stats from the run
// document + the per-property move list from the scenario move log
// (filtered by optimizeRunId).

function formatDays (days = []) {
  const short = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri' }
  return (days || []).map(day => short[day] || day).join('/') || '—'
}

// Tier-2 placements store the PLACED date + factors.offsetDays
// (= placed - due days, cadencePlacementService). Derive the due date with
// UTC math only — no local-midnight Date parsing, so no timezone drift.
function placementDueYmd (placement) {
  const offsetDays = Number(placement?.factors?.offsetDays)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(placement?.date || ''))
  if (!match || !Number.isFinite(offsetDays)) return null
  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) - offsetDays * 86400000
  return new Date(ms).toISOString().slice(0, 10)
}

const UNASSIGNED_SKIP_REASON_LABELS = {
  already_scheduled: 'already scheduled',
  schedule_locked: 'locked',
  long_cycle_skipped: 'long-cycle (cadence placement handles these)',
  unknown_frequency: 'unknown frequency',
  no_service_minutes: 'no service minutes',
  no_coordinates: 'missing coordinates'
}

// under = amber/warning, in = green/success, over = red/error — reuses the
// same pill styling as the rest of this drawer's warning/success chips.
const BAND_STATUS_STYLE = {
  under: { background: '#fbe8d6', color: '#a4502f', label: 'under' },
  in: { background: '#e3eee0', color: '#164a2c', label: 'in band' },
  over: { background: '#fbe8d6', color: '#a4502f', label: 'over' }
}

function BandStatusPill ({ status }) {
  const style = BAND_STATUS_STYLE[status] || BAND_STATUS_STYLE.under
  return (
    <span className="rw-pill" style={{ background: style.background, color: style.color, fontSize: 12, fontWeight: 600 }}>
      {style.label}
    </span>
  )
}

function DeltaPill ({ label, before, after, unit = 'min', downIsGood = true }) {
  const delta = Number(after || 0) - Number(before || 0)
  const good = downIsGood ? delta <= 0 : delta >= 0
  return (
    <span className="rw-pill" style={{
      background: good ? '#e3eee0' : '#fbe8d6',
      color: good ? '#164a2c' : '#a4502f',
      fontSize: 12,
      fontWeight: 600
    }}>
      {label}: {Math.round(Number(before || 0))} → {Math.round(Number(after || 0))} {unit}
      {' '}({delta > 0 ? '+' : ''}{Math.round(delta)})
    </span>
  )
}

function SectionTitle ({ children }) {
  return (
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      color: 'var(--df-ink-4, #97a094)',
      margin: '16px 0 6px'
    }}>
      {children}
    </div>
  )
}

export default function AutoOptimizeRunReport ({ open, onClose, run, moves, movesLoading, url, scenarioId, onEliminationApplied }) {
  const summary = run?.summary || {}
  const finished = run?.finishedAt ? new Date(run.finishedAt) : null
  const started = run?.startedAt ? new Date(run.startedAt) : null
  const durationSec = finished && started ? Math.max(0, Math.round((finished - started) / 1000)) : null
  const allUnassigned = summary.unassigned || []
  const unassigned = allUnassigned.filter(item => item.reason !== 'pinned_day_over_capacity' && item.reason !== 'required_day_over_capacity' && item.reason !== 'access_window_over_capacity')
  const pinnedOverCapacity = allUnassigned.filter(item => item.reason === 'pinned_day_over_capacity')
  const requiredOverCapacity = allUnassigned.filter(item => item.reason === 'required_day_over_capacity')
  const accessWindowOverCapacity = allUnassigned.filter(item => item.reason === 'access_window_over_capacity')
  const overflows = summary.overflowWarnings || []
  const patternShifts = summary.patternShifts || []
  const applyWarnings = summary.applyWarnings || []
  const excluded = summary.excluded || []
  const crewWeekBand = summary.crewWeekBand || []
  const eliminationProposals = summary.eliminationProposals || []
  const eliminationSkipped = summary.eliminationSkipped || []
  // Long-cycle cadence fields only exist on runs made after tier-1/tier-2
  // shipped — every section below is presence-guarded so old runs render
  // exactly as before.
  const longCyclePlacements = summary.longCyclePlacements || []
  const longCycleWarnings = summary.longCycleWarnings || []
  // Opt-in place-unassigned pass — sections only exist on runs started with
  // the "Also place unassigned properties" toggle.
  const unassignedPlaced = summary.unassignedPlaced || []
  const unassignedSkipped = summary.unassignedSkipped?.entries || []
  const unassignedSiblingWarnings = summary.unassignedSiblingWarnings || []
  // Customer day locks — only exist on runs where a property carried
  // requiredDays/preferredDays; every section below is presence-guarded.
  const dayLockConflicts = summary.dayLockConflicts || []
  const preferredDaySlipped = summary.preferredDaySlipped || []
  const dayFieldsSkippedMultiVisit = summary.dayFieldsSkippedMultiVisit || []

  // Per-proposal apply state, keyed by `${routeId}:${weekType}` —
  // { applying, movedCount, errorCode, errorMessage }
  const [eliminationState, setEliminationState] = useState({})

  const applyElimination = async (proposal) => {
    const key = `${proposal.routeId}:${proposal.weekType}`
    setEliminationState(prev => ({ ...prev, [key]: { applying: true } }))
    try {
      const res = await axios.post(
        `${url}/api/hmi/territory/scenarios/${scenarioId}/auto-optimize/apply-elimination`,
        { runId: run?.runId, routeId: proposal.routeId, weekType: proposal.weekType }
      )
      setEliminationState(prev => ({
        ...prev,
        [key]: { applying: false, applied: true, movedCount: res.data?.movedCount ?? 0 }
      }))
      if (onEliminationApplied) onEliminationApplied()
    } catch (err) {
      const data = err.response?.data || {}
      setEliminationState(prev => ({
        ...prev,
        [key]: {
          applying: false,
          errorCode: data.error || null,
          errorMessage: data.message || err.message
        }
      }))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ borderBottom: '1px solid var(--df-line, #e0d8c2)', paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>
            Auto-Optimize Run Report
          </span>
          {run?.status && (
            <span className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-3)', fontSize: 12, fontWeight: 600 }}>
              {run.status}{summary.mode === 'consolidate' ? ' · consolidate' : ''}{summary.dryRun ? ' · dry run' : ''}
            </span>
          )}
          {finished && (
            <span style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>
              {finished.toLocaleString()}{durationSec != null ? ` · ${durationSec}s` : ''}{summary.iterationsRun ? ` · ${summary.iterationsRun} pass${summary.iterationsRun === 1 ? '' : 'es'}` : ''}
            </span>
          )}
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 14 }}>
        {run?.error && (
          <div style={{ background: '#fbe8d6', color: '#a4502f', borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
            Run failed: {run.error}
          </div>
        )}

        {/* Stat row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            ['Moved', summary.movedCount],
            ['Locked/pinned', summary.lockedPinnedCount],
            ['Unchanged', summary.unchangedCount]
          ].map(([label, value]) => (
            <span key={label} className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-2)', fontSize: 12, fontWeight: 600 }}>
              {label}: {value ?? 0}
            </span>
          ))}
          <DeltaPill label="Drive" before={summary.driveMinBefore} after={summary.driveMinAfter} unit="min/fortnight" />
          <DeltaPill label="OT" before={summary.otMinBefore} after={summary.otMinAfter} />
          {Number(summary.ignoredStopCount) > 0 && (
            <span
              className="rw-pill"
              style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-4)', fontSize: 12 }}
              title="Stops on crews outside the included route pool are never touched"
            >
              Ignored: {summary.ignoredStopCount} stops on {summary.ignoredRouteCount} excluded routes
            </span>
          )}
          {Number(summary.withheldLongCycleCount) > 0 && (
            <span
              className="rw-pill"
              style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-2)', fontSize: 12, fontWeight: 600 }}
              title={`${(Number(summary.withheldLongCycleManMin || 0) / 60).toFixed(1)}h of long-cycle service withheld from the solver and re-flowed by cadence placement`}
            >
              {summary.withheldLongCycleCount} flexible long-cycle
            </span>
          )}
        </div>

        {/* Moves */}
        <SectionTitle>Moves {movesLoading ? '' : `(${(moves || []).length})`}</SectionTitle>
        {movesLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <CircularProgress size={22} style={{ color: 'var(--df-primary)' }} />
          </div>
        ) : (moves || []).length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--df-ink-4, #97a094)' }}>
            No moves recorded for this run{summary.dryRun ? ' (dry run — nothing was applied)' : ''}.
          </div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--df-line, #e0d8c2)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: 'var(--df-surface-2, #f4efe4)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>Property</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>From</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>To</th>
                </tr>
              </thead>
              <tbody>
                {(moves || []).map(move => (
                  <tr key={move._id} style={{ borderTop: '1px solid var(--df-line, #e0d8c2)' }}>
                    <td style={{ padding: '6px 10px', color: 'var(--df-ink, #1c2419)', fontWeight: 600 }}>
                      {move.propertyName || `Aspire #${move.aspirePropertyId}`}
                    </td>
                    <td style={{ padding: '6px 10px', color: 'var(--df-ink-3)' }}>
                      {(move.fromRouteName || move.fromCrewName || '—')} · {formatDays(move.fromDaysOfWeek)}
                    </td>
                    <td style={{ padding: '6px 10px', color: move.changedCrew ? '#164a2c' : 'var(--df-ink-3)', fontWeight: move.changedCrew ? 600 : 400 }}>
                      {(move.toRouteName || move.toCrewName || '—')} · {formatDays(move.toDaysOfWeek)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Emptied crew-days (consolidation wins) */}
        {(summary.emptiedCrewDays || []).length > 0 && (
          <>
            <SectionTitle>Crew-days emptied ({summary.emptiedCrewDays.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {summary.emptiedCrewDays.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#e3eee0', color: '#164a2c', fontSize: 12, fontWeight: 600 }}>
                  {item.routeName || 'route'} · {item.weekType}:{item.dayOfWeek}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              These days now have zero stops — candidates for cutting the crew that day.
            </div>
          </>
        )}

        {/* Weekly cap warnings (consolidation can exceed 40 hrs/week) */}
        {(summary.weeklyCapWarnings || []).length > 0 && (
          <>
            <SectionTitle>Crew-weeks over 40 hrs ({summary.weeklyCapWarnings.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {summary.weeklyCapWarnings.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#fbe8d6', color: '#a4502f', fontSize: 12 }}>
                  {item.routeName || 'route'} · week {item.weekType} — {Math.round(item.totalMin)} min (+{Math.round(item.overMin)})
                </span>
              ))}
            </div>
          </>
        )}

        {/* Unassigned */}
        {unassigned.length > 0 && (
          <>
            <SectionTitle>Could not place without OT ({unassigned.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unassigned.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#fbe8d6', color: '#a4502f', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} ({item.weekType})
                  {String(item.stopKey || '').startsWith('unassigned:') ? ' · still unscheduled' : ''}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              These stayed on their current assignment — their service time doesn't fit any crew-day under 8 hours.
              {unassigned.some(item => String(item.stopKey || '').startsWith('unassigned:'))
                ? ' Rejected place-unassigned candidates stay in the unscheduled tray.'
                : ''}
            </div>
          </>
        )}

        {/* Pinned stops whose own crew-day is over capacity (e.g. split-visit days) */}
        {pinnedOverCapacity.length > 0 && (
          <>
            <SectionTitle>Pinned to an over-capacity day ({pinnedOverCapacity.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {pinnedOverCapacity.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-3)', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} ({item.weekType})
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              Split/locked stops left exactly where they were — their crew-day itself exceeds the daily cap.
            </div>
          </>
        )}

        {/* Required-day stops that couldn't fit anywhere on an allowed day */}
        {requiredOverCapacity.length > 0 && (
          <>
            <SectionTitle>Required day over capacity ({requiredOverCapacity.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {requiredOverCapacity.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#fbe8d6', color: '#a4502f', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} ({item.weekType})
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              These stayed on their current assignment — none of the required days had room under 8 hours.
            </div>
          </>
        )}

        {/* Access-window stops that couldn't fit anywhere within their hard arrival/departure window */}
        {accessWindowOverCapacity.length > 0 && (
          <>
            <SectionTitle>Access window over capacity ({accessWindowOverCapacity.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {accessWindowOverCapacity.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#fbe8d6', color: '#a4502f', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} ({item.weekType})
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              These stayed on their current assignment — no crew-day could fit inside the property's access window.
            </div>
          </>
        )}

        {/* Locked/pinned stops whose day contradicts a requiredDays set */}
        {dayLockConflicts.length > 0 && (
          <>
            <SectionTitle>Day requirement conflicts with a lock ({dayLockConflicts.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {dayLockConflicts.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#fbe8d6', color: '#a4502f', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} ({item.weekType}) — locked to {formatDays([item.pinnedDay])}, requires {formatDays(item.requiredDays)}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              Lock wins — unlock the property to let the requirement apply.
            </div>
          </>
        )}

        {/* Repair pass landed a preferred-day stop off its preference */}
        {preferredDaySlipped.length > 0 && (
          <>
            <SectionTitle>Preferred day slipped ({preferredDaySlipped.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {preferredDaySlipped.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-3)', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} ({item.weekType}) — prefers {formatDays(item.preferredDays)} → placed {formatDays([item.placedDay])}
                  {item.routeName ? ` · ${item.routeName}` : ''}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Day preferences/requirements can't bind multi-visit cadences */}
        {dayFieldsSkippedMultiVisit.length > 0 && (
          <>
            <SectionTitle>Day fields skipped — multi-visit ({dayFieldsSkippedMultiVisit.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {dayFieldsSkippedMultiVisit.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-3)', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} ({item.weekType})
                  {item.requiredDays?.length ? ` — requires ${formatDays(item.requiredDays)}` : ''}
                  {item.preferredDays?.length ? ` — prefers ${formatDays(item.preferredDays)}` : ''}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              Day preferences/requirements on multi-visit properties are not yet enforced.
            </div>
          </>
        )}

        {/* Overflow warnings */}
        {overflows.length > 0 && (
          <>
            <SectionTitle>Crew-days still over 8 hrs ({overflows.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {overflows.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#fbe8d6', color: '#a4502f', fontSize: 12 }}>
                  {item.routeName || 'route'} · {item.weekType}:{item.dayOfWeek} — {Math.round(item.totalMin)} min
                </span>
              ))}
            </div>
          </>
        )}

        {/* Crew-week hours vs the 36-42h target band */}
        {crewWeekBand.length > 0 && (
          <>
            <SectionTitle>Crew-week hours (target 36–42h)</SectionTitle>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--df-line, #e0d8c2)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: 'var(--df-surface-2, #f4efe4)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Route</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Week</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Hours</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {crewWeekBand.map((entry, index) => (
                    <tr key={index} style={{ borderTop: '1px solid var(--df-line, #e0d8c2)' }}>
                      <td style={{ padding: '6px 10px', color: 'var(--df-ink, #1c2419)', fontWeight: 600 }}>
                        {entry.routeName || entry.routeId}
                      </td>
                      <td style={{ padding: '6px 10px', color: 'var(--df-ink-3)' }}>{entry.weekType}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--df-ink-3)' }}>{(Number(entry.totalMin || 0) / 60).toFixed(1)}h</td>
                      <td style={{ padding: '6px 10px' }}><BandStatusPill status={entry.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Tier-2 long-cycle cadence placements */}
        {longCyclePlacements.length > 0 && (
          <>
            <SectionTitle>Long-cycle placements ({longCyclePlacements.length})</SectionTitle>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--df-line, #e0d8c2)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: 'var(--df-surface-2, #f4efe4)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Property</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Due → placed</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Route</th>
                    <th style={{ padding: '6px 10px', fontWeight: 700 }}>Headroom</th>
                  </tr>
                </thead>
                <tbody>
                  {longCyclePlacements.map((placement, index) => {
                    const dueYmd = placementDueYmd(placement)
                    return (
                      <tr key={placement.workTicketVisitId || index} style={{ borderTop: '1px solid var(--df-line, #e0d8c2)' }}>
                        <td style={{ padding: '6px 10px', color: 'var(--df-ink, #1c2419)', fontWeight: 600 }}>
                          {placement.propertyName || `Aspire #${placement.aspirePropertyId}`}
                        </td>
                        <td style={{ padding: '6px 10px', color: 'var(--df-ink-3)' }}>
                          {dueYmd && dueYmd !== placement.date
                            ? `${formatShortDate(dueYmd)} → ${formatShortDate(placement.date)}`
                            : formatShortDate(placement.date)}
                        </td>
                        <td style={{ padding: '6px 10px', color: 'var(--df-ink-3)' }}>
                          {placement.routeName || placement.routeId || '—'}
                        </td>
                        <td style={{ padding: '6px 10px', color: 'var(--df-ink-3)' }}>
                          {Number.isFinite(Number(placement.factors?.headroomMin))
                            ? `${Math.round(Number(placement.factors.headroomMin) / 60)}h`
                            : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Long-cycle warnings (no slack in window / write failures) */}
        {longCycleWarnings.length > 0 && (
          <>
            <SectionTitle>Long-cycle warnings ({longCycleWarnings.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {longCycleWarnings.map((warning, index) => (
                warning.reason === 'write_failed' ? (
                  <span key={index} style={{ color: 'var(--df-danger-text, #b3261e)', fontSize: 12.5, fontWeight: 700 }}>
                    Override write failed — {warning.failedCount ?? '?'} placement{warning.failedCount === 1 ? '' : 's'} not persisted
                  </span>
                ) : (
                  <span key={index} className="rw-pill" style={{ background: 'var(--df-danger-bg, #fdecea)', color: 'var(--df-danger-text, #b3261e)', fontSize: 12 }}>
                    #{warning.aspirePropertyId} — {warning.reason === 'no_slack_in_window' ? 'no slack in window' : warning.reason}
                  </span>
                )
              ))}
            </div>
          </>
        )}

        {/* Place-unassigned pass: tray properties given a real slot */}
        {unassignedPlaced.length > 0 && (
          <>
            <SectionTitle>Unassigned placed ({unassignedPlaced.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unassignedPlaced.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#e3eee0', color: '#164a2c', fontSize: 12, fontWeight: 600 }}>
                  {/* daysOfWeek is the applied pattern — multi-visit cadences
                      (D/MWF/TW) show e.g. Mon/Wed/Fri, not just the solved day */}
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} → {item.routeName || 'route'} · {item.weekType === 'both' ? 'A+B' : item.weekType}:{formatDays(item.daysOfWeek || [item.dayOfWeek])}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              Previously unscheduled properties now assigned a crew-day (logged as moves).
            </div>
          </>
        )}

        {/* Fanned-out pattern days the solver never priced that would overflow */}
        {unassignedSiblingWarnings.length > 0 && (
          <>
            <SectionTitle>Pattern days over capacity ({unassignedSiblingWarnings.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unassignedSiblingWarnings.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: '#fbe8d6', color: '#a4502f', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} · {item.routeName || 'route'} · {item.weekType}:{formatDays([item.dayOfWeek])} — {Math.round(item.totalMin)} min
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
              A multi-visit placement books only its solved day in the plan — these sibling pattern days would exceed the daily cap.
            </div>
          </>
        )}

        {/* Place-unassigned pass: tray properties the pass could not feed in */}
        {unassignedSkipped.length > 0 && (
          <>
            <SectionTitle>Unassigned skipped ({unassignedSkipped.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unassignedSkipped.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-3)', fontSize: 12 }}>
                  {item.propertyName || `Aspire #${item.aspirePropertyId}`} — {UNASSIGNED_SKIP_REASON_LABELS[item.reason] || item.reason}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Route elimination proposals (opt-in apply) */}
        {(eliminationProposals.length > 0 || eliminationSkipped.length > 0) && (
          <>
            <SectionTitle>Route elimination proposals ({eliminationProposals.length})</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {eliminationProposals.map((proposal, index) => {
                const key = `${proposal.routeId}:${proposal.weekType}`
                const state = eliminationState[key] || {}
                const alreadyApplied = !!proposal.appliedAt
                const applied = alreadyApplied || state.applied
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 8,
                      padding: '8px 10px',
                      border: '1px solid var(--df-line, #e0d8c2)',
                      borderRadius: 6,
                      fontSize: 12.5
                    }}
                  >
                    <span style={{ color: 'var(--df-ink, #1c2419)' }}>
                      Route {proposal.routeName || proposal.routeId} · week {proposal.weekType} — {(Number(proposal.totalMinAbsorbed || 0) / 60).toFixed(1)}h absorbed by {(proposal.receivingRouteNames || []).filter(Boolean).join(', ') || '—'}
                    </span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={applied || state.applying}
                      onClick={() => applyElimination(proposal)}
                      style={{ fontSize: 11, fontWeight: 700 }}
                    >
                      {state.applying ? <CircularProgress size={14} /> : applied ? 'Applied' : 'Apply elimination'}
                    </Button>
                    {applied && (
                      <span className="rw-pill" style={{ background: '#e3eee0', color: '#164a2c', fontSize: 12, fontWeight: 600 }}>
                        Applied{Number.isFinite(state.movedCount) ? ` · ${state.movedCount} moves` : ''}
                      </span>
                    )}
                    {state.errorCode && (
                      <span style={{ color: '#a4502f', fontSize: 12 }}>
                        {state.errorCode} — {state.errorMessage}
                      </span>
                    )}
                  </div>
                )
              })}
              {eliminationSkipped.map((entry, index) => (
                <div key={`skip-${index}`} style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>
                  Route {entry.routeName || entry.routeId} week {entry.weekType} — skipped ({entry.reason})
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pattern shifts */}
        {patternShifts.length > 0 && (
          <>
            <SectionTitle>Multi-visit day patterns shifted ({patternShifts.length})</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {patternShifts.map((item, index) => (
                <span key={index} className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-3)', fontSize: 12 }}>
                  #{item.aspirePropertyId}: {formatDays(item.fromDays)} → {formatDays(item.toDays)}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Excluded + apply warnings */}
        {(excluded.length > 0 || applyWarnings.length > 0) && (
          <>
            <SectionTitle>Other warnings</SectionTitle>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--df-ink-3)' }}>
              {excluded.map((item, index) => (
                <li key={`ex-${index}`}>Skipped {item.stopKey} — {item.reason === 'no_coordinates' ? 'missing coordinates' : item.reason === 'required_day_no_vehicle' ? 'required day sealed/closed' : item.reason}</li>
              ))}
              {applyWarnings.map((warning, index) => (
                <li key={`aw-${index}`} style={{ color: '#a4502f' }}>{warning}</li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid var(--df-line, #e0d8c2)', padding: '10px 24px' }}>
        <Button onClick={onClose} style={{ color: 'var(--df-ink-3)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
